import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

function CheckoutInner() {
  const navigate = useNavigate();

  const [cartItems, setCartItems] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState("cod");

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    address: "",
    city: "",
    postal: "",
    phone: "",
    cardName: "",
    cardNumber: "",
    expiry: "",
    cvv: "",
  });

  // Load Cart & Validate Session
  useEffect(() => {
    if (sessionStorage.getItem("isLoggedIn") !== "true") {
      navigate("/login-now");
      return;
    }

    const cart = JSON.parse(sessionStorage.getItem("cart")) || [];

    if (cart.length === 0) navigate("/cart");

    setCartItems(cart);
  }, [navigate]);

  const cartTotal = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  // ---- Formatters ----
  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const formatCardNumber = (value) =>
    value
      .replace(/\D/g, "")
      .slice(0, 16)
      .replace(/(.{4})/g, "$1 ")
      .trim();

  const formatExpiry = (value) => {
    let cleaned = value.replace(/\D/g, "").slice(0, 4);
    if (cleaned.length >= 3)
      cleaned = cleaned.replace(/(\d{2})(\d{1,2})/, "$1/$2");
    return cleaned;
  };

  const placeOrder = () => {
    if (!formData.name || !formData.email || !formData.address) {
      alert("Please complete billing details.");
      return;
    }

    if (paymentMethod === "online") {
      if (
        !formData.cardName ||
        formData.cardNumber.length !== 19 ||
        !/^\d{2}\/\d{2}$/.test(formData.expiry) ||
        formData.cvv.length !== 3
      ) {
        alert("Please enter valid card details.");
        return;
      }
    }

    const newOrder = {
      id: Date.now(),
      items: cartItems,
      total: cartTotal,
      method: paymentMethod,
      customer: formData,
    };

    const history = JSON.parse(sessionStorage.getItem("orderHistory")) || [];
    history.push(newOrder);
    sessionStorage.setItem("orderHistory", JSON.stringify(history));

    sessionStorage.removeItem("cart");
    navigate("/order-success");
  };

  return (
    <div className="checkout-wrapper">
      <div className="container">
        <h2 className="checkout-title">Checkout</h2>

        <div className="row">
          {/* Billing Section */}
          <div className="col-lg-6">
            <div className="checkout-box">
              <h4>Your Information</h4>

              <input
                className="form-control"
                placeholder="Full Name *"
                name="name"
                value={formData.name}
                onChange={handleChange}
              />

              <input
                className="form-control"
                placeholder="Email Address *"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />

              <input
                className="form-control"
                placeholder="Phone Number *"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />

              <textarea
                className="form-control"
                rows="3"
                placeholder="Street Address *"
                name="address"
                value={formData.address}
                onChange={handleChange}
              />

              <div className="row">
                <div className="col-6">
                  <input
                    className="form-control"
                    placeholder="City"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-6">
                  <input
                    className="form-control"
                    placeholder="Postal / ZIP"
                    name="postal"
                    value={formData.postal}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Order + Payment */}
          <div className="col-lg-6">
            <div className="checkout-box">
              <h4>Your Order Summary</h4>

              <div className="order-summary">
                {cartItems.map((item) => (
                  <div key={item.id} className="summary-item">
                    <span>
                      {item.name} × {item.quantity}
                    </span>
                    <strong>${(item.price * item.quantity).toFixed(2)}</strong>
                  </div>
                ))}

                <div className="summary-total">
                  <span>Total</span>
                  <strong>${cartTotal.toFixed(2)}</strong>
                </div>
              </div>

              {/* Payment */}
              <div className="payment-section">
                <h5>Payment Method</h5>

                <div
                  className={`payment-select ${
                    paymentMethod === "cod" ? "active" : ""
                  }`}
                  onClick={() => setPaymentMethod("cod")}
                >
                  <span>🚚 Cash on Delivery</span>
                </div>

                <div
                  className={`payment-select ${
                    paymentMethod === "online" ? "active" : ""
                  }`}
                  onClick={() => setPaymentMethod("online")}
                >
                  <span>💳 Online Payment</span>
                </div>

                {paymentMethod === "online" && (
                  <div className="card-wrapper">
                    <input
                      className="form-control"
                      placeholder="Name on Card"
                      name="cardName"
                      onChange={handleChange}
                    />

                    <input
                      className="form-control"
                      placeholder="Card Number"
                      name="cardNumber"
                      value={formData.cardNumber}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          cardNumber: formatCardNumber(e.target.value),
                        })
                      }
                    />

                    <div className="row">
                      <div className="col-6">
                        <input
                          className="form-control"
                          placeholder="MM/YY"
                          name="expiry"
                          value={formData.expiry}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              expiry: formatExpiry(e.target.value),
                            })
                          }
                        />
                      </div>
                      <div className="col-6">
                        <input
                          className="form-control"
                          placeholder="CVV"
                          maxLength="3"
                          name="cvv"
                          value={formData.cvv}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <button className="place-order-btn" onClick={placeOrder}>
                Place Order
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Styling */}
      <style>{`
        .checkout-wrapper{padding:60px 0;background:#f5f7fa;}
        .checkout-title{text-align:center;font-size:32px;font-weight:700;margin-bottom:30px;}
        .checkout-box{background:#fff;border-radius:14px;padding:25px;margin-bottom:20px;box-shadow:0 5px 18px rgba(0,0,0,0.05);}
        .form-control{margin-bottom:14px;padding:12px;border-radius:10px;border:1px solid #ccc;}
        textarea{resize:none;}

        /* Order Summary */
        .order-summary{
          border:1px solid #ddd;
          border-radius:10px;
          padding:15px;
          margin-bottom:20px;
          background:#fafafa;
        }
        .summary-item{
          display:flex;
          justify-content:space-between;
          padding:6px 0;
          border-bottom:1px dashed #ddd;
        }
        .summary-total{
          display:flex;
          justify-content:space-between;
          padding-top:10px;
          font-size:18px;
          font-weight:700;
        }

        /* Payment Options */
        .payment-section h5{margin-bottom:12px;font-size:16px;font-weight:600;}
        .payment-select{
          border:2px solid #ccc;
          padding:12px;
          border-radius:12px;
          margin-bottom:10px;
          cursor:pointer;
          transition:.3s;
          font-weight:500;
          background:#fff;
        }
        .payment-select.active{
          border-color:#0b61d6;
          background:#e7f1ff;
        }

        /* Card Fields */
        .card-wrapper{margin-top:15px;}
        .place-order-btn{
          width:100%;
          padding:14px;
          font-size:18px;
          background:#0b61d6;
          border:none;
          border-radius:10px;
          color:white;
          font-weight:600;
          transition:.3s;
        }
        .place-order-btn:hover{background:#084db5;}
      `}</style>
    </div>
  );
}

export default CheckoutInner;
